import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Lead } from "../types";
import { TIER_EMOJI, SIZE_LABELS } from "../constants";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { LeadScoreBadge } from "./LeadScoreBadge";
import { useLocale } from "../../../context/LanguageContext";

interface LeadsTableProps {
  leads: Lead[];
  total: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSelect: (lead: Lead) => void;
}

export function LeadsTable({
  leads,
  total,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onSelect,
}: LeadsTableProps) {
  const { t } = useLocale();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, total);

  if (isLoading && leads.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
        <span className="text-4xl">📋</span>
        <p className="mt-3 text-sm font-medium text-foreground">
          {t("leads.empty")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("leads.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.name")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.company")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.tier")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.status")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.score")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.source")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("leads.table.created")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {leads.map((lead) => (
              <tr
                key={lead._id}
                onClick={() => onSelect(lead)}
                className="cursor-pointer transition-colors hover:bg-muted/30"
              >
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-foreground">{lead.name}</p>
                    <p className="text-xs text-muted-foreground">{lead.email}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="text-foreground">{lead.company}</p>
                  <p className="text-xs text-muted-foreground">{SIZE_LABELS[lead.size]} {t("leads.form.employees")}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-foreground">
                    {TIER_EMOJI[lead.tier]} {t(`leads.tier.${lead.tier}`)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <LeadStatusBadge status={lead.status} />
                </td>
                <td className="px-4 py-3">
                  <LeadScoreBadge score={lead.qualificationScore} />
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {t(`leads.source.${lead.source}`)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {format(new Date(lead.createdAt), "MMM d, yyyy")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-border px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {t("common.showingRange")
            .replace("{start}", String(startRow))
            .replace("{end}", String(endRow))
            .replace("{total}", String(total))}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-40"
            aria-label={t("common.previousPage")}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-muted-foreground">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page + 1 >= totalPages}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-40"
            aria-label={t("common.nextPage")}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
